import type React from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import WizardWrapper from './WizardWrapper';
import { Box, Button, Heading } from '@zillow/constellation-10';
import OverviewScreen from './enrollmentScreens/OverviewScreen';
import PrivacyNoticeScreen from './enrollmentScreens/PrivacyNoticeScreen';
import { useRentReportingAuth } from '../contexts/RentReportingAuthProvider';
import { ReportingForAllEnrollmentStatus } from '@zg-rentals/ts-rent-credit-report-client';
import {
  ClickstreamAuthTriggerSource,
  ClickstreamTriggerObjectOnboardingScreen,
  emitAuthStartEvent,
  emitOnboardingWizardViewEvent,
} from '../analytics/events';
import router from 'next/router';
import type { WizardScreen } from './WizardWrapper';

export const EnrollmentScreenWrapper = ({ children }: { children: React.ReactNode }) => {
  return <Box css={{ display: 'flex', flexDirection: 'column', gap: '{spacing.800}' }}>{children}</Box>;
};

export default function EnrollmentPage() {
  const { user, login, updateEnrollmentStatus } = useRentReportingAuth();
  const hasEmittedViewEvent = useRef(false);

  useEffect(() => {
    if (!hasEmittedViewEvent.current) {
      hasEmittedViewEvent.current = true;
      emitOnboardingWizardViewEvent();
    }
  }, []);

  const handlePrivacyNoticeNext = useCallback(async () => {
    if (user?.isLoggedIn) {
      await updateEnrollmentStatus(ReportingForAllEnrollmentStatus.INITIATED_ENROLLMENT);
      router.push('/enroll/esusu');
    } else {
      emitAuthStartEvent(ClickstreamAuthTriggerSource.ESUSU_ENROLLMENT);
      // Set query param before login to trigger enrollment status update after login
      login(ClickstreamAuthTriggerSource.ESUSU_ENROLLMENT, 'initiate_enrollment');
    }
  }, [user?.isLoggedIn, updateEnrollmentStatus, login]);

  const screens: Array<WizardScreen> = useMemo(
    () => [
      {
        id: 'overview',
        body: (
          <EnrollmentScreenWrapper>
            <OverviewScreen />
          </EnrollmentScreenWrapper>
        ),
        nextButtonText: 'Continue',
        clickstreamTriggerObject: ClickstreamTriggerObjectOnboardingScreen.START_PAGE,
      },
      {
        id: 'privacyNotice',
        body: (
          <EnrollmentScreenWrapper>
            <PrivacyNoticeScreen />
          </EnrollmentScreenWrapper>
        ),
        nextButtonText: 'Get started',
        nextAction: handlePrivacyNoticeNext,
        clickstreamTriggerObject: ClickstreamTriggerObjectOnboardingScreen.HOW_IT_WORKS,
      },
    ],
    [handlePrivacyNoticeNext],
  );

  const heading = (
    <Box css={{ display: 'flex', justifyContent: 'space-between' }}>
      <Heading level={5} fontFamily="heading" css={{ margin: 'auto 0' }}>
        CreditClimb
      </Heading>
      <Button tone="neutral" asChild>
        <a href="/renting/your-rental">Exit</a>
      </Button>
    </Box>
  );

  return <WizardWrapper screens={screens} defaultHeader={heading} />;
}
